<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">{{ $t('Tickets') }}</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('tickets')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <router-link
                        aria-label="Ticket anlegen"
                        class="btn-stage-icon-plain"
                        :to="ticketNewPath"
                        title="Ticket anlegen"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                    </router-link>
                    <button
                        id="filter-button"
                        :aria-label="$t('Filter menu')"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        type="button"
                        @click="openFiltersSidebar"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                        <span
                            v-if="hasActiveFilters"
                            aria-hidden="true"
                            class="stage-filter-active-dot"
                        ></span>
                    </button>
                    <button
                        :aria-label="$t('Reload')"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        @click="getTickets"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>
        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>
                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="tickets-list">
            <template v-if="ticketList.length > 0">
                <div class="sm:hidden">
                    <ul class="border-b border-gray-200 divide-y divide-gray-border">
                        <template v-for="ticket in ticketList">
                            <li>
                                <router-link
                                    :to="ticketManagePath(ticket.id)"
                                    class="flex items-center justify-between px-4 py-4 sm:px-6"
                                >
                                    <div class="flex items-center truncate space-x-3">
                                        <img
                                            :alt="$t('Avatar')"
                                            :src="getCreatorAvatar(ticket)"
                                            class="h-8 w-8 mr-4 rounded-full"
                                        >
                                        <div class="whitespace-no-wrap">
                                            <div class="text-sm leading-5 text-gray-900">
                                                <span
                                                    v-if="ticketPinningAvailable && ticket.is_pinned"
                                                    class="inline-flex h-5 w-5 items-center justify-center rounded-sm bg-red-600 text-white mr-2 align-middle"
                                                    title="Angepinnt"
                                                >
                                                    <svg-vue class="h-3 w-3" icon="font-awesome.regular.thumbtack"></svg-vue>
                                                </span>
                                                {{ ticket.subject }}
                                                <template v-for="label in ticket.labels">
                                                    <div
                                                        :style="{backgroundColor: label.color}"
                                                        class="stage-ticket-badge ml-1"
                                                    >
                                                        <span class="stage-ticket-badge-label">{{ label.name }}</span>
                                                    </div>
                                                </template>
                                            </div>
                                            <div class="text-sm leading-5 text-gray-500 w-full truncate flex items-center">
                                                <span v-if="shouldShowReplyDot(ticket)" class="inline-block h-2 w-2 rounded-full bg-red-500 mr-2 flex-shrink-0"></span>
                                                <span class="truncate">{{ getTicketPreview(ticket) }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <svg-vue class="ml-4 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-600 transition ease-in-out duration-150" icon="font-awesome.angle-right-regular"></svg-vue>
                                </router-link>
                            </li>
                        </template>
                    </ul>
                </div>
                <div class="hidden sm:flex tickets-list-desktop stage-listview-split-keep-last-border">
                    <div ref="ticketsListHeader" class="tickets-list-header" @scroll.passive="syncTicketsHeaderScroll">
                        <table :class="['tickets-list-table', canViewPriority ? 'tickets-list-table--with-priority' : 'tickets-list-table--without-priority', 'table-listview', 'table-listview--compact-y']">
                            <colgroup>
                                <col class="w-[3.25rem]">
                                <col class="tickets-list-col-ticket">
                                <col class="w-[16rem]">
                                <col class="w-40">
                                <col v-if="canViewPriority" class="w-36">
                                <col class="w-40">
                                <col class="w-44">
                                <col class="w-28">
                            </colgroup>
                            <thead>
                            <tr>
                                <th
                                    class="table-listview-l-th w-[3.25rem] min-w-[3.25rem] max-w-[3.25rem] px-0 whitespace-nowrap text-center border-r border-ultralight font-medium text-ink-muted"
                                    style="overflow: visible; text-overflow: clip;"
                                >
                                    ID
                                </th>
                                <th class="table-listview-l-th tickets-list-col-ticket whitespace-nowrap font-medium text-ink-muted">
                                    {{ $t('Ticket') }}
                                </th>
                                <th class="table-listview-l-th w-[16rem]">
                                    {{ $t('Sender') }}
                                </th>
                                <th class="table-listview-l-th w-40 whitespace-nowrap">
                                    {{ $t('Participants') }}
                                </th>
                                <th v-if="canViewPriority" class="table-listview-l-th w-36 whitespace-nowrap">
                                    {{ $t('Priority') }}
                                </th>
                                <th class="table-listview-l-th w-40 whitespace-nowrap">
                                    {{ $t('Status') }}
                                </th>
                                <th class="table-listview-l-th w-44 whitespace-nowrap">
                                    {{ $t('Update') }}
                                </th>
                                <th class="table-listview-l-th table-listview-l-th-right w-28 min-w-[7rem] whitespace-nowrap">
                                    {{ $t('Actions') }}
                                </th>
                            </tr>
                            </thead>
                        </table>
                    </div>
                    <div ref="ticketsListBody" class="tickets-list-body" @scroll.passive="syncTicketsBodyScroll">
                        <table :class="['tickets-list-table', canViewPriority ? 'tickets-list-table--with-priority' : 'tickets-list-table--without-priority', 'table-listview', 'table-listview--zebra', 'table-listview--compact-y']">
                            <colgroup>
                                <col class="w-[3.25rem]">
                                <col class="tickets-list-col-ticket">
                                <col class="w-[16rem]">
                                <col class="w-40">
                                <col v-if="canViewPriority" class="w-36">
                                <col class="w-40">
                                <col class="w-44">
                                <col class="w-28">
                            </colgroup>
                            <tbody class="bg-white">

                            <template v-for="ticket in ticketList" :key="'ticket-row-' + ticket.id">
                                <tr
                                    class="cursor-pointer"
                                    @click="$router.push(ticketManagePath(ticket.id))"
                                >
                                    <td
                                        class="table-listview-l-td align-middle whitespace-no-wrap w-[3.25rem] min-w-[3.25rem] max-w-[3.25rem] px-0 py-0 text-center border-r border-ultralight"
                                        style="background-color: var(--table-listview-zebra-color);"
                                    >
                                        <div class="flex items-center justify-center h-full min-h-[4.5rem] px-1">
                                            <span class="text-xs leading-4 text-gray-400 font-medium">{{ ticket.id }}</span>
                                        </div>
                                    </td>

                                    <td class="table-listview-l-td max-w-0 whitespace-no-wrap tickets-list-col-ticket ticket-list-ticket-cell">
                                        <span
                                            v-if="ticketPinningAvailable && ticket.is_pinned"
                                            class="ticket-pin-marker"
                                            title="Angepinnt"
                                        >
                                            <svg-vue class="ticket-pin-marker-icon" icon="font-awesome.regular.thumbtack"></svg-vue>
                                        </span>
                                        <div :class="['ticket-cell-content', ticketPinningAvailable && ticket.is_pinned ? 'ticket-cell-content--pinned' : '']">
                                            <div v-if="getTicketProjects(ticket).length > 0" class="flex items-center flex-shrink-0">
                                                <template v-for="(project, projectIndex) in getTicketProjects(ticket)" :key="'ticket-project-' + ticket.id + '-' + project.id">
                                                    <img
                                                        :src="ticketProjectImageUrl(project)"
                                                        :alt="project.title || 'Projektbild'"
                                                        :title="projectTooltip(project)"
                                                        :class="projectIndex === 0 ? '' : '-ml-8'"
                                                        class="h-10 w-10 rounded-sm object-cover border border-white"
                                                    >
                                                </template>
                                            </div>
                                            <div :class="getTicketProjects(ticket).length > 0 ? 'ml-3' : ''" class="min-w-0 flex-1">
                                                <div class="flex items-center text-sm leading-5 font-medium text-gray-900 overflow-hidden">
                                                    <div class="truncate flex items-center min-w-0">
                                                        <span class="truncate">{{ ticket.subject }}</span>
                                                    </div>
                                                    <template v-for="label in ticket.labels">
                                                        <div
                                                            :style="{backgroundColor: label.color}"
                                                            class="stage-ticket-badge inline-flex ml-1 flex-shrink-0"
                                                        >
                                                            <span class="stage-ticket-badge-label">{{ label.name }}</span>
                                                        </div>
                                                    </template>
                                                </div>
                                                <div class="text-sm leading-5 text-gray-500 truncate flex items-center">
                                                    <span v-if="shouldShowReplyDot(ticket)" class="inline-block h-2 w-2 rounded-full bg-red-500 mr-2 flex-shrink-0"></span>
                                                    <span class="truncate">{{ getTicketPreview(ticket) }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="table-listview-l-td w-[16rem] whitespace-no-wrap">
                                        <div class="flex items-center min-w-0">
                                            <div class="flex-shrink-0 h-10 w-10">
                                                <img
                                                    :alt="$t('Avatar')"
                                                    :src="getCreatorAvatar(ticket)"
                                                    class="h-10 w-10 rounded-full"
                                                >
                                            </div>
                                            <div class="ml-4 min-w-0">
                                                <div class="text-sm leading-5 font-medium text-gray-900 truncate">
                                                    {{ getCreatorName(ticket) }}
                                                </div>
                                                <div class="text-sm leading-5 text-gray-500 truncate">
                                                    {{ getCreatorEmail(ticket) }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="table-listview-l-td w-40 whitespace-nowrap">
                                        <div class="flex items-center">
                                            <template v-for="(user, index) in getVisibleAddedUsers(ticket)" :key="'added-user-' + ticket.id + '-' + user.id">
                                                <img
                                                    :alt="$t('Avatar')"
                                                    :src="user.avatar !== 'gravatar' ? user.avatar : user.gravatar"
                                                    :class="index === 0 ? '' : '-ml-3'"
                                                    class="inline-block h-6 w-6 rounded-full border border-white object-cover"
                                                >
                                            </template>
                                            <span
                                                v-if="getRemainingAddedUsersCount(ticket) > 0"
                                                :title="getRemainingAddedUsersCount(ticket) + ' ' + $t('more participants')"
                                                :class="getVisibleAddedUsers(ticket).length > 0 ? '-ml-3' : ''"
                                                class="inline-flex h-6 w-6 items-center justify-center rounded-full border border-white bg-gray-200 text-xs font-semibold text-gray-500"
                                            >
                                                +
                                            </span>
                                            <span v-if="getAddedUsers(ticket).length < 1" class="text-sm leading-5 text-gray-500">
                                                {{ $t('Unassigned') }}
                                            </span>
                                        </div>
                                    </td>
                                    <td v-if="canViewPriority" class="table-listview-l-td w-36 whitespace-nowrap">
                                        <div class="text-sm leading-5 whitespace-nowrap">
                                            <span
                                                v-if="ticket.priority"
                                                :class="isHighPriority(ticket.priority) ? 'inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-semibold leading-4 bg-red-50 text-red-700 border-red-200' : 'text-gray-500'"
                                            >
                                                {{ ticket.priority.name }}
                                            </span>
                                            <span v-else class="text-gray-500">–</span>
                                        </div>
                                    </td>
                                    <td class="table-listview-l-td w-40 whitespace-nowrap">
                                        <div class="text-sm leading-5 text-gray-500 whitespace-nowrap">
                                            {{ ticket.status ? ticket.status.name : $t('Unassigned') }}
                                        </div>
                                    </td>
                                    <td class="table-listview-l-td w-44 whitespace-nowrap">
                                        <div class="text-sm text-gray-500 whitespace-nowrap">
                                            {{ $momentFormatDateTimeAgo(ticket.updated_at) }}
                                        </div>
                                    </td>
                                    <td class="table-listview-l-td w-28 min-w-[7rem] text-right relative" @click.stop>
                                        <div class="inline-flex items-center justify-end w-full relative">
                                            <button
                                                type="button"
                                                class="inline-flex items-center justify-center h-7 w-7 text-gray-500"
                                                @click.stop.prevent="toggleRowActions(ticket.id)"
                                            >
                                                <span class="flex flex-col items-center justify-center space-y-0.5">
                                                    <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                    <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                    <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                </span>
                                            </button>
                                            <div
                                                v-if="activeActionsTicketId === ticket.id"
                                                class="absolute right-0 top-9 z-30 w-56 rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-sm"
                                            >
                                                <button
                                                    type="button"
                                                    class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    @click.stop.prevent="openTicket(ticket)"
                                                >
                                                    {{ $t('Open ticket') }}
                                                </button>
                                                <button
                                                    v-if="ticketPinningAvailable"
                                                    type="button"
                                                    class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    @click.stop.prevent="toggleTicketPin(ticket)"
                                                >
                                                    {{ ticket.is_pinned ? 'Ticket lösen' : 'Ticket anpinnen' }}
                                                </button>
                                                <button
                                                    type="button"
                                                    class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    @click.stop.prevent="markTicketAsUnread(ticket)"
                                                >
                                                    {{ $t('Mark as unread') }}
                                                </button>
                                                <button
                                                    type="button"
                                                    class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    @click.stop.prevent="openTicketInNewWindow(ticket)"
                                                >
                                                    {{ $t('Open in new window') }}
                                                </button>
                                                <button
                                                    type="button"
                                                    class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    @click.stop.prevent="openSenderTickets(ticket)"
                                                >
                                                    {{ $t('Tickets of sender') }}
                                                </button>
                                                <button
                                                    v-if="aiEnabled"
                                                    type="button"
                                                    class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    @click.stop.prevent="selectTicketForSummary(ticket)"
                                                >
                                                    KI-Zusammenfassung anzeigen
                                                </button>
                                            </div>
                                        </div>
                                    </td>
                                </tr>

                            </template>
                            </tbody>
                        </table>
                    </div>
                </div>
            </template>
            <template v-else-if="!loading">
                <empty-state />
            </template>
                    </div>
                    <aside
                        :aria-hidden="!filtersSidebar"
                        class="stage-filter-sidebar stage-filter-panel tickets-filter-sidebar w-80 overflow-auto z-20"
                        :class="filtersSidebar ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                    >
                        <div class="stage-filter-body">
                            <div class="stage-filter-title-row">
                                <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                                <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                            </div>
                            <div class="grid grid-cols-3 py-1">
                                <div class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Sort by') }}</label>
                                    <div class="stage-filter-row">
                                        <button class="stage-filter-sort-btn text-sm leading-5 font-medium" type="button" @click="changeSort">
                                            <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"/>
                                        </button>
                                        <div class="stage-filter-sort-select-wrap">
                                            <select id="sort.column" v-model="sort.column" aria-label="Sort by" class="stage-filter-sort-select block form-select text-sm leading-5 font-medium transition ease-in-out duration-150" @change="changeSort">
                                                <option value="subject">{{ $t('Subject') }}</option>
                                                <option value="status_id">{{ $t('Status') }}</option>
                                                <option v-if="canViewPriority" value="priority_id">{{ $t('Priority') }}</option>
                                                <option value="ticket_topic_id">{{ $t('TicketTopic') }}</option>
                                                <option value="recipient_user_id">Empfänger</option>
                                                <option value="sender_user_id">{{ $t('Sender') }}</option>
                                                <option value="created_at">{{ $t('Created at') }}</option>
                                                <option value="updated_at">{{ $t('Update') }}</option>
                                                <option value="closed_at">{{ $t('Closed at') }}</option>
                                            </select>
                                            <span class="stage-filter-select-icon">
                                                <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Search') }}</label>
                                    <div class="relative flex-grow focus-within:z-10">
                                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                                        </div>
                                        <input id="search" v-model.lazy="filters.search" :placeholder="$t('Search')" aria-label="Search" class="stage-filter-input form-input block pl-10 text-sm transition ease-in-out duration-150" @change="getTickets">
                                    </div>
                                </div>
                                <div class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Einträge') }}</label>
                                    <input-select
                                        class="stage-filter-select"
                                        id="entries"
                                        v-model="filters.entries"
                                        :options="entriesOptions"
                                        option-label="name"
                                        required
                                        @change="getTickets"
                                    />
                                </div>
                                <div v-if="!useReducedTicketFilters && !get_user" class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">Kunden</label>
                                    <input-select
                                        class="stage-filter-select"
                                        id="customer_id"
                                        v-model="filters.customer_id"
                                        :options="customerList"
                                        option-label="name"
                                        @change="getTickets"
                                    >
                                        <template v-slot:selectedOption="props">
                                            <template v-if="!props || !props.anySelected">
                                                <span class="block truncate">Alle Kunden</span>
                                            </template>
                                            <template v-else>
                                                <span class="block truncate">{{ props.option ? props.option.name : 'Alle Kunden' }}</span>
                                            </template>
                                        </template>
                                        <template v-slot:selectOption="props">
                                            <div class="flex items-center space-x-3">
                                                <div class="flex-shrink-0 inline-block">
                                                    <img :src="props.option.avatar !== 'gravatar' ? props.option.avatar : props.option.gravatar" alt="Icon" class="w-5 h-auto rounded-full">
                                                </div>
                                                <div class="block truncate" :class="props.option.id === filters.customer_id ? 'font-semibold' : 'font-normal'">
                                                    {{ props.option.name }}
                                                </div>
                                            </div>
                                        </template>
                                    </input-select>
                                </div>
                                <div v-if="!useReducedTicketFilters" class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Agents') }}</label>
                                    <input-select class="stage-filter-select" id="agent" v-model="filters.agents" :options="agentList" multiple option-label="name" @change="getTickets">
                                        <template v-slot:selectOption="props">
                                            <div class="flex items-center space-x-3">
                                                <div class="flex-shrink-0 inline-block">
                                                    <img :src="props.option.avatar !== 'gravatar' ? props.option.avatar : props.option.gravatar" alt="Icon" class="w-5 h-auto rounded-full">
                                                </div>
                                                <div :class="Object.values(filters.agents).indexOf(props.option.id) > -1 ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                                                    {{ props.option.name }}
                                                </div>
                                            </div>
                                        </template>
                                    </input-select>
                                </div>
                                <div v-if="!useReducedTicketFilters" class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Topic areas') }}</label>
                                    <input-select class="stage-filter-select" id="ticket_topic" v-model="filters.ticket_topics" :options="ticketTopicList" multiple option-label="name" @change="getTickets">
                                        <template v-slot:selectOption="props">
                                            <div class="flex items-center space-x-3">
                                                <input
                                                    type="checkbox"
                                                    class="form-checkbox h-4 w-4 text-gray-600 pointer-events-none"
                                                    :checked="Object.values(filters.ticket_topics).indexOf(props.option.id) > -1"
                                                >
                                                <div class="font-normal block truncate">
                                                    {{ props.option.name }}
                                                </div>
                                            </div>
                                        </template>
                                    </input-select>
                                </div>
                                <div v-if="!useReducedTicketFilters" class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Projects') }}</label>
                                    <input-select class="stage-filter-select" id="projects" v-model="filters.project_id" :options="projectList" option-label="title" @change="getTickets">
                                        <template v-slot:selectOption="props">
                                            <div class="flex items-center space-x-3">
                                                <div class="flex-shrink-0 inline-block">
                                                    <img :src="props.option.image_url" :alt="props.option.title || 'Projektbild'" class="w-5 h-5 rounded object-cover">
                                                </div>
                                                <div :class="Number(props.option.id) === Number(filters.project_id) ? 'font-semibold' : 'font-normal'" class="block truncate">
                                                    {{ props.option.title }}
                                                </div>
                                            </div>
                                        </template>
                                    </input-select>
                                </div>
                                <div v-if="!useReducedTicketFilters" class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Labels') }}</label>
                                    <input-select class="stage-filter-select" id="label" v-model="filters.labels" :options="labelList" multiple option-label="name" @change="getTickets">
                                        <template v-slot:selectedOption="props">
                                            <template v-if="props && !props.anySelected">
                                                <span class="block truncate">{{ $t('Select an option') }}</span>
                                            </template>
                                            <template v-else>
                                                <span class="block truncate">{{ $t('Selected') }} {{ Object.keys(filters.labels).length }} {{ $t('options') }}</span>
                                            </template>
                                        </template>
                                        <template v-slot:selectOption="props">
                                            <div class="flex items-center space-x-3">
                                                <div class="flex-shrink-0 inline-block">
                                                    <svg-vue :style="{color: props.option.color}" class="h-4 w-4" icon="font-awesome.circle-solid"></svg-vue>
                                                </div>
                                                <div :class="Object.values(filters.labels).indexOf(props.option.id) > -1 ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                                                    {{ props.option.name }}
                                                </div>
                                            </div>
                                        </template>
                                    </input-select>
                                </div>
                                <div v-if="!get_statuses" class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Statuses') }}</label>
                                    <input-select class="stage-filter-select" id="status" v-model="filters.statuses" :options="statusList" multiple option-label="name" @change="getTickets">
                                        <template v-slot:selectOption="props">
                                            <div class="flex items-center space-x-3">
                                                <input
                                                    type="checkbox"
                                                    class="form-checkbox h-4 w-4 text-gray-600 pointer-events-none"
                                                    :checked="Object.values(filters.statuses).indexOf(props.option.id) > -1"
                                                >
                                                <div class="font-normal block truncate">
                                                    {{ props.option.name }}
                                                </div>
                                            </div>
                                        </template>
                                    </input-select>
                                </div>
                                <div v-if="canViewPriority && !get_priorities" class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Priorities') }}</label>
                                    <input-select class="stage-filter-select" id="priority" v-model="filters.priorities" :options="priorityList" multiple option-label="name" @change="getTickets">
                                        <template v-slot:selectOption="props">
                                            <div class="flex items-center space-x-3">
                                                <input
                                                    type="checkbox"
                                                    class="form-checkbox h-4 w-4 text-gray-600 pointer-events-none"
                                                    :checked="Object.values(filters.priorities).indexOf(props.option.id) > -1"
                                                >
                                                <div class="font-normal block truncate">
                                                    {{ props.option.name }}
                                                </div>
                                            </div>
                                        </template>
                                    </input-select>
                                </div>
                                <div v-if="!get_perpage" class="col-span-3 mb-3">
                                    <label class="stage-filter-label block">{{ $t('Items per page') }}</label>
                                    <input-select class="stage-filter-select" id="per_page" v-model="perPage" :options="[{id: 10, name: 10}, {id: 25, name: 25}, {id: 50, name: 50}, {id: 100, name: 100}]" option-label="name" required @change="getTickets"/>
                                </div>
                                <div v-if="hasActiveFilters" class="col-span-3">
                                    <button
                                        id="tickets-filter-reset-btn"
                                        type="button"
                                        class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                        @click="resetTicketFilters"
                                    >
                                        {{ $t('Reset selection') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </aside>
                    <aside
                        v-if="aiEnabled"
                        :aria-hidden="!aiSidebar"
                        class="stage-filter-sidebar stage-filter-panel tickets-ai-sidebar w-96 overflow-hidden z-30"
                        :class="aiSidebar ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                    >
                        <div class="stage-filter-body h-full flex flex-col py-4 px-6">
                            <div class="flex items-start justify-between mb-3">
                                <div>
                                    <h2 class="stage-filter-title mb-2">KI-Assistent</h2>
                                </div>
                                <button
                                    type="button"
                                    class="text-gray-400 hover:text-gray-600 ml-3"
                                    @click="aiSidebar = false"
                                >
                                    <svg-vue class="w-4 h-4" icon="font-awesome.times-light"></svg-vue>
                                </button>
                            </div>

                            <div class="tickets-ai-messages flex-1 min-h-0 overflow-y-auto">
                                <div v-if="!selectedAiTicket" class="text-sm text-gray-500 leading-5">
                                    -
                                </div>

                                <div v-else>
                                    <router-link
                                        class="flex items-center gap-2 min-w-0 mb-3"
                                        :to="selectedAiTicket.url"
                                        @click="aiSidebar = false"
                                    >
                                        <img
                                            v-if="selectedAiTicket.creator_avatar"
                                            :src="selectedAiTicket.creator_avatar"
                                            :alt="selectedAiTicket.creator_name || 'Avatar'"
                                            class="h-7 w-7 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                        >
                                        <div
                                            v-else
                                            class="h-7 w-7 rounded-full bg-gray-200 border border-gray-200 flex-shrink-0"
                                        ></div>
                                        <span class="min-w-0 truncate text-sm font-semibold text-gray-800 hover:text-gray-900">
                                            {{ selectedAiTicket.subject || '-' }}
                                        </span>
                                    </router-link>
                                    <div class="relative min-h-[96px] mb-6">
                                        <loading :status="aiLoading"/>
                                        <div
                                            v-if="!aiLoading"
                                            class="text-sm leading-5 text-gray-600 whitespace-pre-line break-words"
                                            style="hyphens: auto;"
                                        >
                                            {{ aiResult.reply || 'Noch keine Zusammenfassung vorhanden.' }}
                                        </div>
                                    </div>

                                    <div
                                        v-if="!aiLoading && aiResult.actions.length > 0"
                                        class="mt-4 bg-white border border-l-4 border-l-orange-200 rounded overflow-hidden"
                                        style="border-color: var(--ui-border-subtle); border-left-color: #f97316;"
                                    >
                                        <div class="px-3 py-2 text-xs font-semibold text-gray-700 tracking-wide" style="background-color: var(--ui-surface-raised);">{{ $t('AI category actions') }}</div>
                                        <div class="px-3">
                                            <div
                                                v-for="(item, index) in aiResult.actions"
                                                :key="'ai-action-' + index"
                                                class="py-2 text-xs leading-5 text-gray-700"
                                                :class="index === 0 ? '' : 'border-t'"
                                                style="border-color: var(--ui-border-subtle);"
                                            >
                                                {{ item }}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        v-if="!aiLoading && aiResult.alerts.length > 0"
                                        class="mt-4 bg-white border border-l-4 border-l-red-200 rounded overflow-hidden"
                                        style="border-color: var(--ui-border-subtle); border-left-color: #ef4444;"
                                    >
                                        <div class="px-3 py-2 text-xs font-semibold text-gray-700 tracking-wide" style="background-color: var(--ui-surface-raised);">{{ $t('AI category alerts') }}</div>
                                        <div class="px-3">
                                            <div
                                                v-for="(item, index) in aiResult.alerts"
                                                :key="'ai-alert-' + index"
                                                class="py-2 text-xs leading-5 text-gray-700"
                                                :class="index === 0 ? '' : 'border-t'"
                                                style="border-color: var(--ui-border-subtle);"
                                            >
                                                {{ item }}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        v-if="!aiLoading && aiResult.warnings.length > 0"
                                        class="mt-4 bg-white border border-l-4 border-l-yellow-200 rounded overflow-hidden"
                                        style="border-color: var(--ui-border-subtle); border-left-color: #eab308;"
                                    >
                                        <div class="px-3 py-2 text-xs font-semibold text-gray-700 tracking-wide" style="background-color: var(--ui-surface-raised);">{{ $t('AI category warnings') }}</div>
                                        <div class="px-3">
                                            <div
                                                v-for="(item, index) in aiResult.warnings"
                                                :key="'ai-warning-' + index"
                                                class="py-2 text-xs leading-5 text-gray-700"
                                                :class="index === 0 ? '' : 'border-t'"
                                                style="border-color: var(--ui-border-subtle);"
                                            >
                                                {{ item }}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        v-if="!aiLoading && aiResult.notes.length > 0"
                                        class="mt-4 bg-white border border-l-4 border-l-blue-200 rounded overflow-hidden"
                                        style="border-color: var(--ui-border-subtle); border-left-color: #3b82f6;"
                                    >
                                        <div class="px-3 py-2 text-xs font-semibold text-gray-700 tracking-wide" style="background-color: var(--ui-surface-raised);">{{ $t('AI category notes') }}</div>
                                        <div class="px-3">
                                            <div
                                                v-for="(item, index) in aiResult.notes"
                                                :key="'ai-note-' + index"
                                                class="py-2 text-xs leading-5 text-gray-700"
                                                :class="index === 0 ? '' : 'border-t'"
                                                style="border-color: var(--ui-border-subtle);"
                                            >
                                                {{ item }}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        v-if="!aiLoading && aiResult.links.length > 0"
                                        class="mt-4 bg-white border rounded overflow-hidden"
                                        style="border-color: var(--ui-border-subtle);"
                                    >
                                        <div class="px-3">
                                            <div
                                                v-for="(link, index) in aiResult.links"
                                                :key="'ai-link-' + index"
                                                class="flex items-start gap-2 py-2"
                                                :class="index === 0 ? '' : 'border-t'"
                                                style="border-color: var(--ui-border-subtle);"
                                            >
                                                <span class="text-gray-500 leading-5 flex-shrink-0" aria-hidden="true">
                                                    <svg class="w-3 h-3 mt-1" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H17a4 4 0 0 1 0 8h-3.5m-3-4H7a4 4 0 0 0 0 8h3.5m-2-8h7" />
                                                    </svg>
                                                </span>
                                                <a
                                                    :href="link.url"
                                                    class="text-xs leading-5 text-gray-700 hover:text-gray-900 break-all"
                                                    rel="noopener noreferrer"
                                                    target="_blank"
                                                >
                                                    {{ link.label }}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
                <list-footer-pagination
                    :start="(pagination.perPage * pagination.currentPage) - pagination.perPage + 1"
                    :end="pagination.perPage * pagination.currentPage <= pagination.total ? pagination.perPage * pagination.currentPage : pagination.total"
                    :total="pagination.total"
                    :current-page="pagination.currentPage"
                    :total-pages="pagination.totalPages"
                    nav-class="stage-list-footer-no-top-border"
                    @prev="changePage(pagination.currentPage - 1)"
                    @next="changePage(pagination.currentPage + 1)"
                />
            </div>
        </div>

    </div>
</template>

<script>
import {resolveAccessState} from "@/utils/access-state";
import {refreshPwaBadgeCount} from "@/utils/pwa-badge";
import {canViewTicketPriority} from "@/utils/ticket-access";
import {
    queryHasKey,
    queryNumber,
    queryNumberArray,
    queryString,
    queryWithoutKeys,
    routeWithReturnTo,
    sameNumberArray,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";

export default {
    name: "list",
    metaInfo() {
        return {
            title: this.$i18n.t('Tickets')
        }
    },
    data() {

        // # n.stirner - filter by get param ----------------
        var routeQuery = (this.$route && this.$route.query) ? this.$route.query : {};

        // initial value
        var initPerPage = 50;
        var initStatuses = [1,2];
        var initPriorities = [];

        // items per page
        var hasListPerPage = queryHasKey(routeQuery, 'list_per_page');
        var hasLegacyPerPage = queryHasKey(routeQuery, 'perpage');
        var perpage = queryNumber(routeQuery, hasListPerPage ? 'list_per_page' : 'perpage', initPerPage, [10, 25, 50, 100]);
        this.get_perpage = hasLegacyPerPage && !hasListPerPage; // if legacy get parameter is set no filter output in filtermenu

        // user
        var user = queryString(routeQuery, 'user', '');
        this.get_user = user !== ''; // if get parameter is set no filter output in filtermenu
        var customerId = queryNumber(routeQuery, ['list_customer_id', 'customer_id'], null);
        customerId = customerId !== null && Number(customerId) > 0 ? Number(customerId) : null;
        var relationshipUserId = queryNumber(routeQuery, 'list_relationship_user_id', null);
        relationshipUserId = relationshipUserId !== null && Number(relationshipUserId) > 0
            ? Number(relationshipUserId)
            : null;
        var projectId = queryNumber(
            routeQuery,
            queryHasKey(routeQuery, 'list_projects') ? 'list_projects' : ['projects[]', 'projects'],
            null
        );
        projectId = projectId !== null && Number(projectId) > 0 ? Number(projectId) : null;

        // Statuses
        var hasListStatuses = queryHasKey(routeQuery, 'list_statuses');
        var hasLegacyStatuses = queryHasKey(routeQuery, 'statuses');
        var statuses = hasListStatuses
            ? queryNumberArray(routeQuery, 'list_statuses', [])
            : (hasLegacyStatuses ? queryNumberArray(routeQuery, 'statuses', initStatuses) : initStatuses);
        this.get_statuses = hasLegacyStatuses && !hasListStatuses; // if legacy get parameter is set no filter output in filtermenu

        // Priorities
        var hasListPriorities = queryHasKey(routeQuery, 'list_priorities');
        var hasLegacyPriorities = queryHasKey(routeQuery, 'priorities');
        var priorities = hasListPriorities
            ? queryNumberArray(routeQuery, 'list_priorities', [])
            : (hasLegacyPriorities ? queryNumberArray(routeQuery, 'priorities', initPriorities) : initPriorities);
        this.get_priorities = hasLegacyPriorities && !hasListPriorities; // if legacy get parameter is set no filter output in filtermenu
        var sortColumn = queryString(routeQuery, 'list_sort_column', 'updated_at');
        if (['subject', 'status_id', 'priority_id', 'ticket_topic_id', 'recipient_user_id', 'sender_user_id', 'created_at', 'updated_at', 'closed_at'].indexOf(sortColumn) === -1) {
            sortColumn = 'updated_at';
        }
        var sortOrder = queryString(routeQuery, 'list_sort_order', 'desc');
        if (['asc', 'desc'].indexOf(sortOrder) === -1) {
            sortOrder = 'desc';
        }
        var page = queryNumber(routeQuery, 'list_page', 1);
        page = page !== null && page > 0 ? page : 1;
        var entries = queryString(routeQuery, 'list_entries', 'all');
        if (['all', 'unread'].indexOf(entries) === -1) {
            entries = 'all';
        }

        // -------------------------------------------

        return {
            loading: true,
            filtersSidebar: false,
            aiSidebar: false,
            aiLoading: false,
            selectedAiTicket: null,
            aiResult: {
                reply: '',
                actions: [],
                alerts: [],
                warnings: [],
                notes: [],
                links: [],
            },
            aiEnabled: false,
            filters: {
                search: queryString(routeQuery, 'list_search', ''),
                entries: entries,
                user: user,
                customer_id: customerId,
                relationship_user_id: relationshipUserId,
                agents: queryNumberArray(routeQuery, 'list_agents', []),
                ticket_topics: queryNumberArray(routeQuery, 'list_ticket_topics', []),
                project_id: projectId,
                labels: queryNumberArray(routeQuery, 'list_labels', []),
                statuses: statuses,
                priorities: priorities,
            },            sort: {
                order: sortOrder,
                column: sortColumn,
            },
            page: page,
            perPage: perpage,
            pagination: {
                currentPage: 0,
                perPage: 0,
                total: 0,
                totalPages: 0
            },
            entriesOptions: [
                {id: 'all', name: 'Alle Einträge'},
                {id: 'unread', name: 'Ungelesene Einträge'},
            ],
            agentList: [],
            customerList: [],
            ticketTopicList: [],
            projectList: [],
            labelList: [],
            statusList: [],
            priorityList: [],
            ticketList: [],
            ticketPinningAvailable: false,
            activeActionsTicketId: null,
            isSyncingTicketListScroll: false,
        }
    },
    computed: {
        currentUser() {
            return this.$store && this.$store.state ? this.$store.state.user : null;
        },
        accessState() {
            return resolveAccessState(this.$store);
        },
        canUseRelationshipFilter() {
            return !!this.accessState.accessContext?.modules?.relationships;
        },
        isCustomerTicketRoute() {
            return String(this.$route.path || '').startsWith('/tickets');
        },
        ticketListPath() {
            return this.isCustomerTicketRoute ? '/tickets/list' : '/workspace/tickets';
        },
        ticketNewPath() {
            return this.isCustomerTicketRoute ? '/tickets/new' : '/workspace/tickets/new';
        },
        canViewPriority() {
            return canViewTicketPriority(this.accessState);
        },
        useReducedTicketFilters() {
            return !!this.accessState.isGuest;
        },
        anyFilter() {
            return this.filters.search !== ''
                || this.filters.entries !== 'all'
                || this.filters.user !== ''
                || this.filters.customer_id !== null
                || (this.canUseRelationshipFilter && this.filters.relationship_user_id !== null)
                || this.filters.agents !== 0
                || this.filters.ticket_topics.length !== 0
                || Number(this.filters.project_id || 0) > 0
                || this.filters.labels.length !== 0
                || this.filters.statuses.length !== 0
                || (this.canViewPriority && this.filters.priorities.length !== 0);
        },
        hasActiveFilters() {
            const hasSearch = (this.filters.search || '').trim() !== '';
            const hasEntriesFilter = String(this.filters.entries || 'all') !== 'all';
            const hasCustomer = Number(this.filters.customer_id || 0) > 0;
            const hasRelationshipUser = this.canUseRelationshipFilter
                && Number(this.filters.relationship_user_id || 0) > 0;
            const hasAgents = Array.isArray(this.filters.agents) && this.filters.agents.length > 0;
            const hasTicketTopics = Array.isArray(this.filters.ticket_topics) && this.filters.ticket_topics.length > 0;
            const hasProjects = Number(this.filters.project_id || 0) > 0;
            const hasLabels = Array.isArray(this.filters.labels) && this.filters.labels.length > 0;
            const hasStatuses = !this.get_statuses
                && Array.isArray(this.filters.statuses)
                && !sameNumberArray(this.filters.statuses, [1, 2]);
            const hasPriorities = this.canViewPriority && !this.get_priorities && Array.isArray(this.filters.priorities) && this.filters.priorities.length > 0;
            const hasSortChange = this.sort.order !== 'desc' || this.sort.column !== 'updated_at';
            const hasPerPageChange = !this.get_perpage && Number(this.perPage || 0) !== 50;

            return hasSearch
                || hasEntriesFilter
                || hasCustomer
                || hasRelationshipUser
                || hasAgents
                || hasTicketTopics
                || hasProjects
                || hasLabels
                || hasStatuses
                || hasPriorities
                || hasSortChange
                || hasPerPageChange;
        },
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    mounted() {
        if (!this.canUseRelationshipFilter) {
            this.filters.relationship_user_id = null;
        }
        this.getTickets();
        this.getFilters();
        document.addEventListener('click', this.handleDocumentClick);
        this.resetTicketsListHorizontalScroll();
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    methods: {
        ticketManagePath(ticketId) {
            return this.isCustomerTicketRoute
                ? routeWithReturnTo('/tickets/' + ticketId, this.$route, this.ticketListPath)
                : routeWithReturnTo('/workspace/tickets/' + ticketId + '/manage', this.$route, this.ticketListPath);
        },
        handleDocumentClick() {
            this.activeActionsTicketId = null;
        },
        resetTicketsListHorizontalScroll() {
            const header = this.$refs.ticketsListHeader;
            const body = this.$refs.ticketsListBody;

            if (header) {
                header.scrollLeft = 0;
            }

            if (body) {
                body.scrollLeft = 0;
            }
        },
        syncTicketsHeaderScroll() {
            if (this.isSyncingTicketListScroll) {
                return;
            }

            const header = this.$refs.ticketsListHeader;
            const body = this.$refs.ticketsListBody;

            if (!header || !body) {
                return;
            }

            this.isSyncingTicketListScroll = true;
            body.scrollLeft = header.scrollLeft;
            requestAnimationFrame(() => {
                this.isSyncingTicketListScroll = false;
            });
        },
        syncTicketsBodyScroll() {
            if (this.isSyncingTicketListScroll) {
                return;
            }

            const header = this.$refs.ticketsListHeader;
            const body = this.$refs.ticketsListBody;

            if (!header || !body) {
                return;
            }

            this.isSyncingTicketListScroll = true;
            header.scrollLeft = body.scrollLeft;
            requestAnimationFrame(() => {
                this.isSyncingTicketListScroll = false;
            });
        },
        toggleRowActions(ticketId) {
            this.activeActionsTicketId = this.activeActionsTicketId === ticketId ? null : ticketId;
        },
        openTicket(ticket) {
            this.activeActionsTicketId = null;
            this.$router.push(this.ticketManagePath(ticket.id));
        },
        openTicketInNewWindow(ticket) {
            this.activeActionsTicketId = null;
            window.open(this.ticketManagePath(ticket.id), '_blank');
        },
        markTicketAsUnread(ticket) {
            const self = this;
            axios.post('api/workspace/tickets/' + ticket.id + '/mark-unread').then(function () {
                ticket.has_unread_reply = true;
                self.activeActionsTicketId = null;
                refreshPwaBadgeCount().catch(function () {
                });
                if (self.$notify) {
                    self.$notify({
                        type: 'success',
                        text: self.$t('Data updated correctly'),
                    });
                }
            });
        },
        toggleTicketPin(ticket) {
            if (!this.ticketPinningAvailable) {
                this.activeActionsTicketId = null;
                return;
            }

            const self = this;
            const wasPinned = Boolean(ticket?.is_pinned);
            const request = wasPinned
                ? axios.delete('api/workspace/tickets/' + ticket.id + '/pin')
                : axios.post('api/workspace/tickets/' + ticket.id + '/pin');

            request.then(function (response) {
                ticket.is_pinned = !wasPinned;
                ticket.pinned_at = response.data?.pinned_at || null;
                self.activeActionsTicketId = null;
                self.getTickets();
                if (self.$notify) {
                    self.$notify({
                        type: 'success',
                        text: self.$t('Data updated correctly'),
                    });
                }
            });
        },
        openSenderTickets(ticket) {
            this.activeActionsTicketId = null;
            const userName = this.getCreatorName(ticket);
            window.open(this.ticketListPath + '?user=' + encodeURIComponent(userName), '_blank');
        },
        syncTicketListRouteState() {
            const selectedCustomerId = typeof this.filters.customer_id === 'object'
                ? Number(this.filters.customer_id?.id || 0)
                : Number(this.filters.customer_id || 0);
            const listRouteKeys = [
                'list_page',
                'list_per_page',
                'list_sort_column',
                'list_sort_order',
                'list_search',
                'list_entries',
                'list_customer_id',
                'list_relationship_user_id',
                'list_agents',
                'list_ticket_topics',
                'list_projects',
                'list_labels',
                'list_statuses',
                'list_priorities',
                'customer_id',
                'projects',
                'projects[]',
            ];
            const nextQuery = queryWithoutKeys(this.$route.query || {}, listRouteKeys);

            setQueryValue(nextQuery, 'list_page', Number(this.page || 1), 1);
            if (!this.get_perpage) {
                setQueryValue(nextQuery, 'list_per_page', Number(this.perPage || 50), 50);
            }
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column, 'updated_at');
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order, 'desc');
            setQueryValue(nextQuery, 'list_search', this.filters.search, '');
            setQueryValue(nextQuery, 'list_entries', this.filters.entries, 'all');
            setQueryValue(nextQuery, 'list_customer_id', selectedCustomerId > 0 ? selectedCustomerId : null);
            setQueryValue(
                nextQuery,
                'list_relationship_user_id',
                this.canUseRelationshipFilter && Number(this.filters.relationship_user_id || 0) > 0
                    ? Number(this.filters.relationship_user_id)
                    : null
            );
            setQueryValue(nextQuery, 'list_agents', this.filters.agents, []);
            setQueryValue(nextQuery, 'list_ticket_topics', this.filters.ticket_topics, []);
            setQueryValue(nextQuery, 'list_projects', this.filters.project_id, null);
            setQueryValue(nextQuery, 'list_labels', this.filters.labels, []);
            if (!this.get_statuses) {
                setQueryValue(nextQuery, 'list_statuses', this.filters.statuses, [1, 2]);
            }
            if (this.canViewPriority && !this.get_priorities) {
                setQueryValue(nextQuery, 'list_priorities', this.filters.priorities, []);
            }

            if (sameRouteQuery(this.$route.query || {}, nextQuery)) {
                return;
            }

            const routeReplace = this.$router.replace({
                path: this.$route.path,
                query: nextQuery,
            });

            if (routeReplace && typeof routeReplace.catch === 'function') {
                routeReplace.catch(function () {
                });
            }
        },

        openFiltersSidebar() {
            if (!this.filtersSidebar) {
                this.aiSidebar = false;
            }
            this.filtersSidebar = !this.filtersSidebar;
        },
        selectTicketForSummary(ticket) {
            this.activeActionsTicketId = null;
            this.selectedAiTicket = {
                id: Number(ticket.id),
                subject: String(ticket.subject || ''),
                url: this.ticketManagePath(ticket.id),
                creator_name: this.getCreatorName(ticket),
                creator_avatar: this.getCreatorAvatar(ticket),
            };
            this.aiResult = {
                reply: '',
                actions: [],
                alerts: [],
                warnings: [],
                notes: [],
                links: [],
            };
            this.aiSidebar = true;
            this.filtersSidebar = false;
            this.runAiAction();
        },
        normalizeAiStringList(value) {
            const values = Array.isArray(value) ? value : [value];
            return values
                .map((entry) => String(entry || '').trim())
                .filter((entry) => entry !== '');
        },
        normalizeAiLinks(value) {
            const values = Array.isArray(value) ? value : [];
            return values.map((entry) => {
                if (entry && typeof entry === 'object') {
                    const label = String(entry.label || '').trim();
                    const url = this.normalizeAiLinkUrl(String(entry.url || '').trim());
                    return {
                        label: label || url,
                        url: url || '#',
                    };
                }
                const raw = this.normalizeAiLinkUrl(String(entry || '').trim());
                return {
                    label: raw,
                    url: raw || '#',
                };
            }).filter((entry) => entry.url && entry.url !== '#');
        },
        normalizeAiLinkUrl(rawUrl) {
            const url = String(rawUrl || '').trim();
            if (url === '') {
                return '';
            }

            const lower = url.toLowerCase();
            const isFileApiLink = lower.includes('/api/files/download/') || lower.includes('/api/files/preview/');
            if (!isFileApiLink) {
                return url;
            }

            if (lower.includes('token=')) {
                return url;
            }

            const token = typeof getAuthToken === 'function' ? getAuthToken() : '';
            if (!token) {
                return url;
            }

            return url + (url.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(token);
        },
        normalizeAiEnabled(value) {
            return value === true || value === 1 || value === '1';
        },
        runAiAction() {
            if (this.aiLoading || !this.selectedAiTicket) {
                return;
            }
            this.aiLoading = true;

            axios.post('api/workspace/tickets/ai-assistant', {
                ticket_id: this.selectedAiTicket.id,
            }).then((response) => {
                this.aiResult = {
                    reply: String(response.data?.reply || response.data?.summary || response.data?.answer || 'Es konnte keine Zusammenfassung erstellt werden.'),
                    actions: this.normalizeAiStringList(response.data?.actions || response.data?.action_items),
                    alerts: this.normalizeAiStringList(response.data?.alerts),
                    warnings: this.normalizeAiStringList(response.data?.warnings),
                    notes: this.normalizeAiStringList(response.data?.notes),
                    links: this.normalizeAiLinks(response.data?.links),
                };
            }).catch((error) => {
                const message = String(error?.response?.data?.message || 'Die Anfrage konnte nicht verarbeitet werden.');
                this.aiResult = {
                    reply: 'Fehler: ' + message,
                    actions: [],
                    alerts: [],
                    warnings: [],
                    notes: [],
                    links: [],
                };
            }).finally(() => {
                this.aiLoading = false;
            });
        },
        changePage(page) {
            const self = this;
            if ((page > 0) && (page <= self.pagination.totalPages) && (page !== self.page)) {
                self.page = page;
                self.getTickets();
            }
        },
        changeSort() {
            const self = this;
            if (self.sort.order === 'asc') {
                self.sort.order = 'desc';
            } else if (self.sort.order === 'desc') {
                self.sort.order = 'asc';
            }
            self.getTickets();
        },
        resetTicketFilters() {
            this.filters.search = '';
            this.filters.entries = 'all';
            if (!this.get_user) {
                this.filters.user = '';
            }
            this.filters.customer_id = null;
            this.filters.relationship_user_id = null;
            this.filters.agents = [];
            this.filters.ticket_topics = [];
            this.filters.project_id = null;
            this.filters.labels = [];
            if (!this.get_statuses) {
                this.filters.statuses = [];
            }
            if (this.canViewPriority && !this.get_priorities) {
                this.filters.priorities = [];
            }
            this.page = 1;
            this.getTickets();
        },
        getTickets() {
            const self = this;
            self.loading = true;
            self.syncTicketListRouteState();
            const selectedCustomerId = typeof self.filters.customer_id === 'object'
                ? Number(self.filters.customer_id?.id || 0)
                : Number(self.filters.customer_id || 0);
            const sortPayload = {
                ...self.sort,
                column: !self.canViewPriority && self.sort.column === 'priority_id'
                    ? 'updated_at'
                    : self.sort.column,
            };
            axios.get('api/workspace/tickets', {
                params: {
                    page: self.page,
                    sort: sortPayload,
                    perPage: self.perPage,
                    search: self.filters.search,
                    entries: self.filters.entries,
                    user: self.filters.user,
                    customer_id: selectedCustomerId > 0 ? selectedCustomerId : null,
                    relationship_user_id: self.canUseRelationshipFilter
                        ? self.filters.relationship_user_id
                        : null,
                    agents: self.filters.agents,
                    ticket_topics: self.filters.ticket_topics,
                    projects: Number(self.filters.project_id || 0) > 0
                        ? [Number(self.filters.project_id)]
                        : [],
                    labels: self.filters.labels,
                    statuses: self.filters.statuses,
                    priorities: self.canViewPriority ? self.filters.priorities : [],
                }
            }).then(function (response) {
                self.ticketList = response.data.items;
                self.ticketPinningAvailable = Boolean(response.data.ticket_pinning_available);
                self.pagination = response.data.pagination;
                refreshPwaBadgeCount().catch(function () {
                });
                if (self.pagination.totalPages < self.pagination.currentPage) {
                    self.page = self.pagination.totalPages;
                    self.getTickets();
                } else {
                    self.loading = false;
                    self.$nextTick(() => {
                        self.resetTicketsListHorizontalScroll();
                    });
                }
            }).catch(function () {
                self.loading = false;
            });
        },
        getFilters() {
            const self = this;
            axios.get('api/workspace/tickets/filters').then(function (response) {
                if (!self.useReducedTicketFilters) {
                    self.agentList = response.data.agents;
                    self.customerList = response.data.customers || [];
                    self.ticketTopicList = response.data.ticket_topics;
                    self.projectList = Array.isArray(response.data.projects)
                        ? response.data.projects
                        : ((response.data.projects && Array.isArray(response.data.projects.data)) ? response.data.projects.data : []);
                    self.labelList = response.data.labels;
                }

                self.statusList = response.data.statuses;
                self.priorityList = response.data.priorities;
                if (!self.canViewPriority) {
                    self.filters.priorities = [];
                    if (self.sort.column === 'priority_id') {
                        self.sort.column = 'updated_at';
                    }
                }
                self.aiEnabled = self.normalizeAiEnabled(response.data.ai_enabled);
                if (!self.aiEnabled) {
                    self.aiSidebar = false;
                    self.selectedAiTicket = null;
                    self.aiResult = {
                        reply: '',
                        actions: [],
                        alerts: [],
                        warnings: [],
                        notes: [],
                        links: [],
                    };
                }
            });
        },
        getTicketPreview(ticket) {
            if (!ticket || !ticket.lastReply || !ticket.lastReply.body) {
                return '-';
            }

            const stripped = String(ticket.lastReply.body)
                .replace(/<[^>]+>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();

            if (!stripped) {
                return '-';
            }

            if (typeof document === 'undefined') {
                return stripped
                    .replace(/&nbsp;/gi, ' ')
                    .replace(/&amp;/gi, '&')
                    .replace(/&quot;/gi, '"')
                    .replace(/&#39;/gi, "'")
                    .replace(/&lt;/gi, '<')
                    .replace(/&gt;/gi, '>')
                    .replace(/\s+/g, ' ')
                    .trim() || '-';
            }

            const decoder = document.createElement('textarea');
            decoder.innerHTML = stripped;

            return (decoder.value || decoder.textContent || '')
                .replace(/\u00a0/g, ' ')
                .replace(/\s+/g, ' ')
                .trim() || '-';
        },
        getTicketProjects(ticket) {
            const projects = Array.isArray(ticket?.projects)
                ? ticket.projects
                : (ticket?.projects && Array.isArray(ticket.projects.data) ? ticket.projects.data : []);
            if (projects.length > 0) {
                return projects
                    .filter((project) => project && Number(project.id) > 0);
            }

            if (ticket && ticket.project && Number(ticket.project.id) > 0) {
                return [ticket.project];
            }

            return [];
        },
        ticketProjectImageUrl(project) {
            if (project && project.image_url) {
                return project.image_url;
            }

            return '/images/default/icon.png';
        },
        projectTooltip(project) {
            const title = String(project?.title || '').trim();
            return title !== '' ? `Projekt: ${title}` : 'Projekt';
        },
        getCreator(ticket) {
            return ticket?.creator || ticket?.sender || null;
        },
        getCreatorName(ticket) {
            return String(this.getCreator(ticket)?.name || '');
        },
        getCreatorEmail(ticket) {
            return String(this.getCreator(ticket)?.email || '');
        },
        getCreatorAvatar(ticket) {
            const creator = this.getCreator(ticket);
            if (creator?.avatar && creator.avatar !== 'gravatar') {
                return creator.avatar;
            }

            return String(creator?.gravatar || '');
        },
        getAddedUsers(ticket) {
            const currentCreatorId = Number(ticket?.creator?.id || ticket?.creator_user_id || ticket?.sender?.id || ticket?.sender_user_id || 0);
            const currentRecipientId = Number(ticket?.recipient?.id || ticket?.recipient_user_id || 0);
            const byId = new Map();
            const push = (user) => {
                const id = Number(user?.id || 0);
                if (!id || id === currentCreatorId || byId.has(id)) {
                    return;
                }
                byId.set(id, user);
            };

            if (currentRecipientId) {
                push(ticket?.recipient);
            }

            const mainAdmin = ticket?.main_admin || this.getMainAdmin();
            const mainAdminId = Number(mainAdmin?.id || 0);
            if (mainAdminId && mainAdminId !== currentRecipientId) {
                push(mainAdmin);
            }

            [...(ticket.agents || []), ...(ticket.customers || []), ...(ticket.guests || [])].forEach((user) => {
                push(user);
            });

            return [...byId.values()];
        },
        getVisibleAddedUsers(ticket) {
            return this.getAddedUsers(ticket).slice(0, 5);
        },
        getRemainingAddedUsersCount(ticket) {
            return Math.max(this.getAddedUsers(ticket).length - 5, 0);
        },
        getMainAdmin() {
            const admins = (this.agentList || [])
                .filter((user) => Number(user?.role_id) === 1)
                .sort((a, b) => Number(a.id) - Number(b.id));

            return admins.length > 0 ? admins[0] : null;
        },
        shouldShowReplyDot(ticket) {
            return Boolean(ticket?.has_unread_reply);
        },
        isHighPriority(priority) {
            const value = Number(priority?.value || 0);
            const name = String(priority?.name || '').toLowerCase();
            return value >= 3
                || name.includes('high')
                || name.includes('hoch')
                || name.includes('urgent')
                || name.includes('dring');
        },
    }
}
</script>

<style lang="scss">
@reference "../../../../../sass/tailwind-reference.css";

.tickets-list {
    @apply bg-white w-full h-full z-0 min-h-0 overflow-y-auto;
}

@media (min-width: theme(--breakpoint-sm)) {
    .tickets-list {
        overflow: hidden;
    }

    .tickets-list-desktop {
        @apply flex flex-col h-full min-h-0;
    }

    .tickets-list-header {
        @apply flex-shrink-0;
        background-color: var(--ui-surface-raised);
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: none;
        -ms-overflow-style: none;
    }

    .tickets-list-header::-webkit-scrollbar {
        display: none;
    }

    .tickets-list-body {
        @apply flex-1 min-h-0 overflow-y-auto;
        overflow-x: auto;
        scrollbar-gutter: stable;
    }

    .tickets-list-table {
        table-layout: fixed;
        width: 100%;
    }

    .tickets-list-col-ticket {
        min-width: 24%;
    }

    .tickets-list-table--with-priority .tickets-list-col-ticket {
        width: max(24%, calc(100% - 66.25rem));
    }

    .tickets-list-table--without-priority .tickets-list-col-ticket {
        width: max(24%, calc(100% - 57.25rem));
    }

    .tickets-list-table--with-priority {
        min-width: 94.75rem;
    }

    .tickets-list-table--without-priority {
        min-width: 81.75rem;
    }
}

.ticket-pin-marker {
    @apply inline-flex items-center justify-center bg-red-600 text-white;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 2rem;
}

.ticket-pin-marker-icon {
    @apply text-white;
    width: 0.9375rem;
    height: 0.9375rem;
    color: #ffffff;
}

.table-listview-l-td.ticket-list-ticket-cell {
    padding: 0 !important;
    position: relative;
    overflow: hidden;
}

.ticket-cell-content {
    @apply flex items-center min-w-0 px-5 py-1;
    min-height: 4.5rem;
}

.ticket-cell-content--pinned {
    padding-left: 3.25rem;
}

.filters-options {
    @apply rounded bg-white shadow-xs p-3 overflow-y-auto;
    height: calc(100vh - 270px);
    max-height: 360px;
}

.tickets-filter-sidebar .stage-filter-select > div > .inline-block {
    box-shadow: none;
    border-radius: 4px;
}

.tickets-filter-sidebar .stage-filter-select > div > .inline-block > button {
    border-color: var(--ui-border-default);
    border-radius: 4px;
    box-shadow: none;
}

.tickets-filter-sidebar .stage-filter-select > div > .absolute {
    border-radius: 4px;
    box-shadow: none;
    border: 1px solid var(--ui-border-default);
}

.tickets-filter-sidebar .stage-filter-select > div > .absolute > ul {
    box-shadow: none;
}

.tickets-ai-sidebar .stage-filter-title {
    margin-bottom: 0;
}

.tickets-ai-messages {
    min-height: 240px;
}

@media (min-width: theme(--breakpoint-md)) {
    .filters-options {
        height: calc(100vh - 140px)
    }
}
// # n.stirner - style context menu
</style>
